import dayjs from "dayjs";
import { fhirClients } from "src-next/shared/api/fhirClient";
import {
  INVITE_APPOINTMENT_IDENTIFIER_SYSTEM,
  INVITE_PROVIDER_IDENTIFIER_SYSTEM,
  type InviteResponse,
  type InviteTaskStatus,
} from "../constants";
import { buildInviteResponseTask } from "../transformers/inviteResponseTaskTransformers";
import {
  inviteAppointmentId,
  isInvitePending,
  isTeleconsultoriaInviteTask,
} from "../helpers/inviteTaskHelpers";

const TASK_RESOURCE_TYPE = "Task";
const OPEN_INVITES_SEARCH_COUNT = "50";

/** Grava a Task de convite no workflow (mesmo servidor do `PostTask` legado). */
export function createInviteTask(task: fhir5.Task): Promise<fhir5.Task> {
  return fhirClients.workflow.create<fhir5.Task>(TASK_RESOURCE_TYPE, task);
}

/**
 * Avisa quem convidou que o convite foi aceito/recusado (Task de notificação).
 * Best effort: falha aqui não desfaz o aceite/recusa já gravados.
 */
export async function notifyInviteResponse(params: {
  inviteTask: fhir5.Task;
  response: InviteResponse;
  appointmentStart?: string;
}): Promise<void> {
  const notification = buildInviteResponseTask(params);
  if (!notification) return;
  try {
    await createInviteTask(notification);
  } catch (error) {
    console.error("notifyInviteResponse", error);
  }
}

/**
 * Convites ainda aguardando resposta para o Appointment.
 * Busca pelo `identifier` do Appointment (ele fica em outro servidor, então não
 * existe referência/`focus` para pesquisar). Se o servidor recusar a busca, não
 * bloqueia o convite (devolve lista vazia) e registra no console.
 */
export async function findPendingInviteTasks(
  appointmentId: string,
): Promise<fhir5.Task[]> {
  if (!appointmentId) return [];
  try {
    const bundle = await fhirClients.workflow.search<fhir5.Task>(TASK_RESOURCE_TYPE, {
      identifier: `${INVITE_APPOINTMENT_IDENTIFIER_SYSTEM}|${appointmentId}`,
      status: "requested",
      _count: OPEN_INVITES_SEARCH_COUNT,
    });
    return (bundle.entry || [])
      .map((entry) => entry.resource)
      .filter((resource): resource is fhir5.Task =>
        isTeleconsultoriaInviteTask(resource),
      )
      .filter(
        (task) => inviteAppointmentId(task) === appointmentId && isInvitePending(task),
      );
  } catch (error) {
    console.error("findPendingInviteTasks", error);
    return [];
  }
}

const PROVIDER_INVITES_SEARCH_COUNT = "100";

/**
 * Convites para os profissionais da PRESTADORA (visão da clínica).
 * Busca pelo `identifier` da prestadora e confere no cliente (code + identifier).
 * Erro de busca é propagado: a tela mostra erro, sem listar outra coisa.
 */
export async function listProviderInviteTasks(providerToken: string): Promise<fhir5.Task[]> {
  if (!providerToken) return [];
  const bundle = await fhirClients.workflow.search<fhir5.Task>(TASK_RESOURCE_TYPE, {
    identifier: `${INVITE_PROVIDER_IDENTIFIER_SYSTEM}|${providerToken}`,
    _sort: "-_lastUpdated",
    _count: PROVIDER_INVITES_SEARCH_COUNT,
  });
  const isFromProvider = (task: fhir5.Task) =>
    Boolean(
      task.identifier?.some(
        (identifier) =>
          identifier.system === INVITE_PROVIDER_IDENTIFIER_SYSTEM &&
          identifier.value === providerToken,
      ),
    );
  return (bundle.entry || [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.Task =>
        isTeleconsultoriaInviteTask(resource) && isFromProvider(resource as fhir5.Task),
    );
}

const REQUESTER_INVITES_SEARCH_COUNT = "200";

/**
 * Convites enviados pelos PractitionerRoles informados (visão da afiliada:
 * status das reservas). `requester` é parâmetro padrão de Task (usado no legado).
 * Erro é propagado: quem chama decide o fallback.
 */
export async function listInviteTasksByRequesters(
  requesterRoleReferences: string[],
): Promise<fhir5.Task[]> {
  const references = requesterRoleReferences.filter(Boolean);
  if (!references.length) return [];
  const bundle = await fhirClients.workflow.search<fhir5.Task>(TASK_RESOURCE_TYPE, {
    requester: references.join(","),
    _sort: "-_lastUpdated",
    _count: REQUESTER_INVITES_SEARCH_COUNT,
  });
  return (bundle.entry || [])
    .map((entry) => entry.resource)
    .filter((resource): resource is fhir5.Task => isTeleconsultoriaInviteTask(resource));
}

/** Lê a Task atual no servidor (a da tela pode estar desatualizada). */
export function readInviteTask(taskId: string): Promise<fhir5.Task> {
  return fhirClients.workflow.get<fhir5.Task>(TASK_RESOURCE_TYPE, taskId);
}

/** Troca o status do convite (aceito, recusado ou cancelado). */
export async function patchInviteStatus(
  taskId: string,
  status: InviteTaskStatus,
): Promise<void> {
  if (!taskId) throw new Error("Convite sem id");
  await fhirClients.workflow.patch<fhir5.Task>(TASK_RESOURCE_TYPE, taskId, [
    { op: "replace", path: "/status", value: status },
    { op: "add", path: "/lastModified", value: dayjs().toISOString() },
  ]);
}
